"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { useHotkeys } from "react-hotkeys-hook";
import { useLocalStorage } from "@uidotdev/usehooks";

import { Button } from "@/components/ui/button";
import { imagineStore } from "@/stores/imagine.store";

type PendingChangesBarProps = {
  hasPendingDrafts: boolean;
  draftStateKey: string;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
  saveDisabled: boolean;
  discardDisabled: boolean;
  delay?: number;
};

const FOOTER_HEADER_HEIGHT = 50;
const DEFAULT_FOOTER_EXPANDED_HEIGHT = 240;

export function PendingChangesBar({
  hasPendingDrafts,
  draftStateKey,
  onSave,
  onDiscard,
  isSaving,
  saveDisabled,
  discardDisabled,
  delay = 800,
}: PendingChangesBarProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);
  const hasShownRef = useRef(false);
  const canTriggerSave = !saveDisabled && !isSaving;

  const canvasFooterOpen = imagineStore.use.ui.canvasFooterOpen();

  /**
   * The user's saved footer height from localStorage.
   * Synced with CanvasPanelFooter to match its height when open.
   * Used to position the PendingChangesBar above the footer dynamically.
   */
  const [localStorageOpenHeight] = useLocalStorage<number>(
    "canvas-footer-open-height",
    DEFAULT_FOOTER_EXPANDED_HEIGHT,
  );

  const footerHeight = canvasFooterOpen
    ? localStorageOpenHeight
    : FOOTER_HEADER_HEIGHT;
  const bottomPosition = footerHeight + 16; // 16px gap above footer

  // Cmd/Ctrl + S triggers save when possible
  useHotkeys(
    ["meta+s", "ctrl+s"],
    (event: KeyboardEvent) => {
      if (!canTriggerSave) {
        return;
      }

      event.preventDefault();
      onSave();
    },
    {
      enableOnFormTags: true,
      enabled: canTriggerSave,
      preventDefault: true,
    },
    [onSave, canTriggerSave],
  );

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!hasPendingDrafts) {
      setVisible(false);
      hasShownRef.current = false;
      return;
    }

    if (hasShownRef.current) {
      setVisible(true);
      return;
    }

    setVisible(false);
    timerRef.current = window.setTimeout(() => {
      setVisible(true);
      hasShownRef.current = true;
      timerRef.current = null;
    }, delay);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasPendingDrafts, draftStateKey, delay]);

  return (
    <motion.div
      initial={false}
      animate={{
        opacity: visible ? 1 : 0,
        y: visible ? 0 : 12,
        bottom: bottomPosition,
      }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="absolute left-72 right-0 flex justify-center px-4"
      style={{ pointerEvents: visible ? "auto" : "none" }}
    >
      <div className="relative w-full max-w-xl">
        <div
          className={
            visible
              ? "absolute inset-x-8 -bottom-2 h-5 rounded-full bg-muted/40 blur-xl dark:bg-accent/30"
              : "absolute inset-x-8 -bottom-2 h-5 rounded-full bg-transparent"
          }
        />
        <div className="relative flex items-center justify-center gap-6 rounded-2xl border border-border bg-card px-6 py-3 text-sm shadow-[0_18px_32px_-24px_rgba(15,23,42,0.35)] dark:shadow-[0_22px_40px_-26px_rgba(0,0,0,0.65)]">
          <span className="text-sm font-medium text-muted-foreground">
            Unsaved changes
          </span>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto rounded-[14px] px-0 text-sm font-medium text-muted-foreground hover:bg-transparent hover:text-foreground dark:hover:text-accent-foreground"
              disabled={discardDisabled || isSaving}
              onClick={onDiscard}
            >
              Discard
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 rounded-[14px] border-border/80 bg-card px-3 text-sm font-medium text-muted-foreground shadow-none hover:bg-card hover:text-foreground dark:border-accent/50 dark:bg-accent dark:text-accent-foreground dark:hover:bg-accent/90"
              disabled={saveDisabled}
              loading={isSaving}
              loadingText="Saving..."
              onClick={onSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
